import logger from '../utils/logger';
import React, { useState, useContext, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Link, useNavigate } from 'react-router-dom';
import { GlobalStateContext } from '../utils/GlobalStateContext';
import 'react-datepicker/dist/react-datepicker.css';
import '../style/Scheduling.css';
import { convertUTCToCentral, dateOfBirth, formatDateOfBirth, formatTimeZone, formatTo12Hour, invertDOB, formatDateForAPI, formatTimePlusFiveHours, getUserId, formatDatetimeForAPI, convertUTCToLocal, formatUTCForDisplay, formatTimeForDisplay, convertLocalToUTC, formatAppointmentTimeRange } from '../utils/utils';
import { InputMask } from '@react-input/mask';
import NewImagingOrder from './NewImagingOrder';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Heart, CalendarClock, CalendarOff, CalendarHeart, CalendarDays, DoorOpen, Search, Home, UserCheck, FlaskConical, Pencil, LogOut } from 'lucide-react';
import PageTopBar from './customComponents/PageTopBar';
import LabReferralView from './customComponents/LabReferralView';
import PatientAvatar from './customComponents/PatientAvatar';


const BASE_URL = process.env.REACT_APP_API_URL;

// Clinic hours — appointments only run 9am-6pm, so no time picker on this
// page should offer anything outside that window (react-datepicker's
// minTime/maxTime only look at the hours/minutes, not the date, so any day
// works as the carrier).
const APPOINTMENT_MIN_TIME = new Date();
APPOINTMENT_MIN_TIME.setHours(9, 0, 0, 0);
const APPOINTMENT_MAX_TIME = new Date();
APPOINTMENT_MAX_TIME.setHours(18, 0, 0, 0);

const api = {
  getExams: async (visitType) => {
    const exams = {
      'Clinic': ['Consultation', 'Follow-up'],
      'Radiology': ['X-Ray', 'US', 'MRI', 'CT', 'DEXA'],
      'Lab': ['Blood', 'Urine', 'Stool', 'Plasma'],
      'Physical': ['Physical Exam'],
      'Procedure': ['Suture', 'Incision', 'Injection']
    };
    return (exams[visitType] || []).map(name => ({ id: name, name }));
  },
  getOrders: async (summaryDate, user, type) => {
    try {
      const dateOnly = invertDOB(dateOfBirth(summaryDate?.toISOString()));
      const response = await fetch(`${BASE_URL}/scheduling/selectDate?scheduled_time=${dateOnly}&user=${user}&type=${type}`);
      const data = await response.json();
      if (data?.length > 0) return data;
    } catch (error) {
      logger.error("Error fetching data", error);
      return [];
    }
  },
  getMonthOrders: async (year, month, user, type) => {
    try {
      const response = await fetch(`${BASE_URL}/scheduling/month?year=${year}&month=${month}&user=${user}&type=${type}`);
      const data = await response.json();
      logger.log(data)
      return data || [];
    } catch (error) {
      logger.error("Error fetching month data", error);
      return [];
    }
  },
  scheduleAppointment: async (appointmentData, user, type) => {
    try {
      const response = await fetch(`${BASE_URL}/scheduling/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointmentData),
        user,
        type
      });
      return response;
    } catch (error) {
      logger.error("Error scheduling appointment", error);
      throw error;
    }
  },
  scheduleReturningPatient: async (appointmentData, user, type) => {
    try {
      const response = await fetch(`${BASE_URL}/scheduling/scheduleReturningPatient`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointmentData),
        user,
        type
      });
      return response;
    } catch (error) {
      logger.error("Error scheduling returning patient appointment", error);
      throw error;
    }
  }
};

const SCREENING_PACKAGES = [
  { 
    id: 'heartHealth', 
    name: 'Heart Health', 
    description: 'Comprehensive cardiac screening', 
    icon: '❤️',
    price: 349,
    duration: 60, // minutes
    exams: [
      { name: 'Cardiac Calcium Score', modality: 'CT', code: "75571", region: "Chest", body_part: "Heart"},
      { name: 'Limited Echocardiogram', modality: 'US', code: "93660", region: "Chest", body_part: "Heart"},
      { name: 'EKG', modality: 'Lab', type: "EKG", code: "93000" }
    ]
  },
  { 
    id: 'vascular', 
    name: 'Vascular', 
    description: 'Vascular health assessment', 
    icon: '🩸',
    price: 349,
    duration: 60,
    exams: [
      { name: 'Limited Carotid Doppler (US Neck)', modality: 'US', code: "93880", region: "Head/Neck", body_part: "Neck" },
      { name: 'Limited Arterial of Extremity (US Legs)', modality: 'US', code: "93926", region: "Lower Extremity", body_part: "Legs" },
      { name: 'Abdominal Aortic (US Abdomen)', modality: 'US', code: "76706", region: "Abdomen/Pelvis", body_part: "Abdomen" }
      // { name: 'Abdominal Aorta (US Abdomen)', modality: 'US', code: "74185", region: "Abdomen / Pelvis", body_part: "Abdomen" }
    ]
  },
  { 
    id: 'aneurysm', 
    name: 'Aneurysm', 
    description: 'Aneurysm detection screening', 
    icon: '🫀',
    price: 1099,
    duration: 60,
    exams: [
      { name: 'MRA Brain Circle of Willis', modality: 'MRI', code: "70544", region: "Head/Neck", body_part: "Brain" },
      { name: 'Abdominal Aortic (US Abdomen)', modality: 'US', code: "76706", region: "Abdomen/Pelvis", body_part: "Abdomen" },
      { name: 'CTA Chest w/o & w/ Contrast', modality: 'CT', code: "71275", region: "Chest", body_part: "Chest" }
    ]
  },
  { 
    id: 'fullBodyMRI', 
    name: 'Full Body MRI', 
    description: 'Complete body scan (excludes breasts)', 
    icon: '🏥',
    price: 1499,
    duration: 120, // 2 hours
    exams: [
      { name: 'Non-contrast Full Body MRI', modality: 'MRI', code: "77084", region: "Full Body", body_part: "Full Body" }
    ]
  },
  { 
    id: 'labsPackage', 
    name: 'Labs Package', 
    description: 'Comprehensive blood work panel', 
    icon: '🧪',
    price: 275,
    duration: 0, // No scheduling blocking needed
    exams: [
      { name: 'Lipid Panel Plus', modality: 'Lab', type: "Blood", code: "80061" },
      { name: 'Thyroid Panel (TSH)', modality: 'Lab', type: "Blood", code: "84443" },
      { name: 'Hemoglobin A1C', modality: 'Lab', type: "Blood", code: "86800" },
      { name: 'Comprehensive Metabolic Panel', modality: 'Lab', type: "Blood", code: "80053" }
    ]
  },
  { 
    id: 'colonography', 
    name: 'CT Colonography', 
    description: 'CT Colonography (Abdomen/Pelvis)', 
    icon: '🔬',
    price: 899,
    duration: 60,
    exams: [
      { name: 'CT Colonography (Abdomen/Pelvis)', modality: 'CT', code: "74263", region: "Abdomen/Pelvis", body_part: "Colon" }
    ]
  },
  { 
    id: 'lungCT', 
    name: 'Lung Screening', 
    description: 'Low dose lung CT screening', 
    icon: '🫁',
    price: 299,
    duration: 60,
    exams: [
      { name: 'Low Dose Lung CT', modality: 'CT', code: "71250", region: "Chest", body_part: "Lungs" }
    ]
  },
  {
    id: 'calciumScoring',
    name: 'Cardiac Calcium Scoring',
    description: 'Heart disease risk assessment',
    icon: '💊',
    price: 199,
    duration: 60,
    exams: [
      { name: 'Cardiac Calcium Score', modality: 'CT', code: "75571", region: "Chest", body_part: "Heart" },
      { name: 'Lipid Panel Plus', modality: 'Lab', type: "Blood", code: "80061" }
    ]
  },
  {
    id: 'dexa',
    name: 'DEXA Scan',
    description: 'Bone density screening',
    icon: '🦴',
    price: 199,
    duration: 30,
    exams: [
      { name: 'DEXA Scan', modality: 'DEXA', code: "77080", region: "Full Body", body_part: "Spine/Hip" }
    ]
  }
];

// Modern Calendar Component with Day Selection
function CalendarView({ currentMonth, setCurrentMonth, appointments, onDateClick, selectedDate, setSelectedDate, monthAvailability = {} }) {
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  
  const getAppointmentsForDay = (day) => {
    return appointments.filter(appt => {
      // Use appointment_start if available, fallback to scheduled_time for legacy appointments
      const appointmentTime = appt.appointment_start || appt.scheduled_time;
      if (!appointmentTime) return false;
      
      const apptDate = convertUTCToLocal(appointmentTime);
      return apptDate.getDate() === day && 
             apptDate.getMonth() === month && 
             apptDate.getFullYear() === year;
    });
  };

  const handleDayClick = (day) => {
    const clickedDate = new Date(year, month, day);
    // Toggle selection - if same date clicked again, deselect
    if (selectedDate && 
        selectedDate.getDate() === day && 
        selectedDate.getMonth() === month && 
        selectedDate.getFullYear() === year) {
      setSelectedDate(null); // Deselect - show all appointments
    } else {
      setSelectedDate(clickedDate); // Select this date
    }
    
    if (onDateClick) {
      onDateClick(clickedDate);
    }
  };

  const renderCalendar = () => {
    const days = [];
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    weekDays.forEach(day => {
      days.push(
        <div key={`header-${day}`} className="calendar-header-day">
          {day}
        </div>
      );
    });
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dayAppointments = getAppointmentsForDay(day);
      const isToday = new Date().getDate() === day &&
                      new Date().getMonth() === month &&
                      new Date().getFullYear() === year;

      const isSelected = selectedDate &&
                        selectedDate.getDate() === day &&
                        selectedDate.getMonth() === month &&
                        selectedDate.getFullYear() === year;

      // Coverage warnings from the Staff Availability page (/availability) — a
      // resource unavailable for the whole day gets the hard badge. Either way
      // POST /scheduling/orderSchedule and PUT /scheduling/reschedule refuse an
      // appointment that overlaps the affected hours.
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayAvailability = monthAvailability[dateStr];
      const isClinicClosed = !!dayAvailability?.clinicClosed;
      const unavailableLabels = [];
      if (!isClinicClosed) {
        if (dayAvailability?.blockedModalities?.length > 0) {
          unavailableLabels.push(...dayAvailability.blockedModalities);
        }
        if (dayAvailability?.allLabsBlocked) {
          unavailableLabels.push('Labs');
        } else if (dayAvailability?.blockedSpecimens?.length > 0) {
          unavailableLabels.push(...dayAvailability.blockedSpecimens.map(s => `${s} labs`));
        }
      }

      days.push(
        <div
          key={`day-${day}`}
          className={`calendar-day ${isToday ? 'today' : ''} ${dayAppointments.length > 0 ? 'has-appointments' : ''} ${isSelected ? 'selected' : ''} ${isClinicClosed ? 'clinic-closed' : ''}`}
          onClick={() => handleDayClick(day)}
        >
          <span className="day-number">{day}</span>
          {dayAppointments.length > 0 && (
            <div className="appointment-dots">
              <span className="dot-count">{dayAppointments.length}</span>
            </div>
          )}
          {isClinicClosed ? (
            <div className="calendar-day-closed-badge" title={dayAvailability.closureReason ? `Clinic closed — ${dayAvailability.closureReason}` : 'Clinic closed'}>
              Clinic Closed
            </div>
          ) : unavailableLabels.length > 0 && (
            <div className="calendar-day-unavailable-badge" title={`No staff available: ${unavailableLabels.join(', ')}`}>
              {unavailableLabels.join(', ')} unavailable
            </div>
          )}
        </div>
      );
    }
    
    return days;
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setSelectedDate(null); // Clear selection when changing months
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setSelectedDate(null); // Clear selection when changing months
  };

  return (
    <div className="modern-calendar">
      <div className="calendar-controls">
        <button onClick={prevMonth} className="calendar-nav-btn">‹</button>
        <h3 className="calendar-month-title">
          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h3>
        <button onClick={nextMonth} className="calendar-nav-btn">›</button>
      </div>
      {selectedDate && (
        <div className="selected-date-banner">
          <span>
            Showing appointments for {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric' 
            })}
          </span>
          <button 
            className="clear-selection-btn" 
            onClick={() => setSelectedDate(null)}
          >
            Show All
          </button>
        </div>
      )}
      <div className="calendar-grid">
        {renderCalendar()}
      </div>
    </div>
  );
}

function ScreeningPackages({ onScheduleComplete }) {
  const [selectedPackages, setSelectedPackages] = useState({});
  const [bookedSlots, setBookedSlots] = useState([]);
  const [currentPackageForSlots, setCurrentPackageForSlots] = useState(null);
  const [packageAppointments, setPackageAppointments] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isNewPatient, setIsNewPatient] = useState(false);
  const [patientInfo, setPatientInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    dob: '',
    sex: ''
  });
  const [appointmentsForDate, setAppointmentsForDate] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const { currentUser } = useContext(GlobalStateContext);
  const user = getUserId(currentUser);

  // Keyed by 'YYYY-MM-DD' -> { clinicClosed, blockedModalities, allLabsBlocked,
  // blockedSpecimens } — same shape as the main calendar's monthAvailability, so
  // a package's date/time picker can't offer a clinic-closed day or a day nobody
  // can run one of that package's modalities.
  const [unavailableDates, setUnavailableDates] = useState({});

  const fetchUnavailableDates = async (monthDate) => {
    try {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const [closureRes, modalityRes, labRes] = await Promise.all([
        fetch(`${BASE_URL}/scheduling/clinicClosures?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/modalityAvailability?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/labAvailability?startDate=${startDate}&endDate=${endDate}`),
      ]);
      const closures = closureRes.ok ? await closureRes.json() : [];
      const modalityData = modalityRes.ok ? await modalityRes.json() : {};
      const labData = labRes.ok ? await labRes.json() : {};

      setUnavailableDates(prev => {
        const merged = { ...prev };
        closures.forEach(c => {
          const d = c.blocked_date.split('T')[0];
          merged[d] = { ...(merged[d] || {}), clinicClosed: true };
        });
        Object.keys(modalityData).forEach(d => {
          merged[d] = { ...(merged[d] || {}), blockedModalities: modalityData[d].blockedModalities };
        });
        Object.keys(labData).forEach(d => {
          merged[d] = {
            ...(merged[d] || {}),
            allLabsBlocked: labData[d].allBlocked,
            blockedSpecimens: labData[d].blockedSpecimens,
          };
        });
        return merged;
      });
    } catch (error) {
      logger.error('Error fetching unavailable dates', error);
    }
  };

  useEffect(() => {
    fetchUnavailableDates(new Date());
  }, []);

  // Why (if at all) a package can't be booked on `date` — clinic closed, or
  // none of the package's own modalities can be performed that day. Returns
  // null when the date is bookable.
  const getPackageDateUnavailableReason = (packageId, date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const info = unavailableDates[dateStr];
    if (!info) return null;
    if (info.clinicClosed) return 'Clinic closed';
    const packageModalities = getPackageModalities(packageId);
    if (packageModalities.length > 0 && packageModalities.every(m => info.blockedModalities?.includes(m))) {
      return `No staff available to run ${packageModalities.join('/')}`;
    }
    return null;
  };
  const isPackageDateUnavailable = (packageId, date) => getPackageDateUnavailableReason(packageId, date) !== null;

  const handleTogglePackage = (packageId) => {
    setSelectedPackages(prev => {
      const newSelected = {
        ...prev,
        [packageId]: !prev[packageId]
      };
      
      if (!newSelected[packageId]) {
        setPackageAppointments(prevAppts => {
          const updated = { ...prevAppts };
          delete updated[packageId];
          return updated;
        });
      }
      
      return newSelected;
    });
  };

  const searchPatients = async (query) => {
    try {
      const response = await fetch(`${BASE_URL}/ris/search?pat=${query}&user=${user}&type=${currentUser.type}`);
      const data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return [];
      }
    } catch (error) {
      logger.error("Error fetching patients:", error);
      return [];
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setPatients([]);
      return;
    }
    const results = await searchPatients(query);
    setPatients(results);
  };

  const fetchAppointmentsForDate = async (date) => {
    if (!date) {
      setAppointmentsForDate([]);
      return;
    }

    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await fetch(`${BASE_URL}/scheduling/screeningAppointmentsForDate?date=${dateStr}`);
      if (response.ok) {
        const data = await response.json();
        setAppointmentsForDate(data || []);
      } else {
        setAppointmentsForDate([]);
      }
    } catch (error) {
      logger.error("Error fetching appointments for date", error);
      setAppointmentsForDate([]);
    }
  };

  const getPackageModalities = (packageId) => {
    const pkg = SCREENING_PACKAGES.find(p => p.id === packageId);
    if (!pkg) return [];

    return pkg.exams
      .map(exam => exam.modality)
      .filter(modality => modality !== 'Lab');
  };

  const checkModalityConflictsWithState = (packageId, selectedTime, appointmentsState = packageAppointments) => {
  if (!selectedTime) return [];

  const pkg = SCREENING_PACKAGES.find(p => p.id === packageId);
  if (!pkg) return [];

  const packageModalities = getPackageModalities(packageId);
  const packageDuration = pkg.duration || 60;
  
  const selectedStart = new Date(selectedTime);
  const selectedEnd = new Date(selectedTime.getTime() + packageDuration * 60000);

  const conflicts = [];

  const timeRangesOverlap = (start1, end1, start2, end2) => {
    return start1 < end2 && start2 < end1;
  };

  // Check conflicts with existing appointments (from appointmentsForDate)
  appointmentsForDate.forEach(appt => {
    const appointmentTime = appt.appointment_start || appt.scheduled_time;
    const apptStart = convertUTCToLocal(appointmentTime);
    const apptDuration = appt.duration || 60;
    const apptEnd = new Date(apptStart.getTime() + apptDuration * 60000);

    if (timeRangesOverlap(selectedStart, selectedEnd, apptStart, apptEnd)) {
      const apptModalities = appt.modalities.filter(m => m !== 'Lab');
      const conflictingModalities = packageModalities.filter(m => apptModalities.includes(m));

      if (conflictingModalities.length > 0) {
        conflicts.push({
          patient: `${appt.first_name} ${appt.last_name}`,
          time: apptStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
          modalities: conflictingModalities.join(', '),
          ris_patients_id: appt.ris_patients_id,
          reason: 'Modality conflict'
        });
      }
    }
  });

  // Check conflicts with other packages using the PROVIDED state (not packageAppointments)
  Object.keys(appointmentsState).forEach(otherPkgId => {
    if (otherPkgId === packageId) return;

    const otherAppt = appointmentsState[otherPkgId];
    if (!otherAppt || !otherAppt.date) return;

    const otherPkg = SCREENING_PACKAGES.find(p => p.id === otherPkgId);
    if (!otherPkg) return;

    const otherStart = new Date(otherAppt.date);
    const otherDuration = otherPkg.duration || 60;
    const otherEnd = new Date(otherStart.getTime() + otherDuration * 60000);

    if (timeRangesOverlap(selectedStart, selectedEnd, otherStart, otherEnd)) {
      const otherModalities = getPackageModalities(otherPkgId);
      const conflictingModalities = packageModalities.filter(m => otherModalities.includes(m));

      const hasModalityConflict = conflictingModalities.length > 0;

      conflicts.push({
        patient: 'Self (Different Package)',
        time: otherStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
        modalities: hasModalityConflict ? conflictingModalities.join(', ') : 'N/A',
        exam: otherPkg.name,
        reason: hasModalityConflict 
          ? 'Modality conflict' 
          : 'Cannot schedule multiple packages simultaneously',
        duration: `${otherDuration} min`
      });
    }
  });

  return conflicts;
};

  const checkModalityConflicts = (packageId, selectedTime) => {
    if (!selectedTime) return [];

    const pkg = SCREENING_PACKAGES.find(p => p.id === packageId);
    if (!pkg) return [];

    const packageModalities = getPackageModalities(packageId);
    const packageDuration = pkg.duration || 60; // Default to 60 minutes if not specified
    
    // Calculate the time range for the selected package
    const selectedStart = new Date(selectedTime);
    const selectedEnd = new Date(selectedTime.getTime() + packageDuration * 60000);

    const conflicts = [];

    // Helper function to check if two time ranges overlap
    const timeRangesOverlap = (start1, end1, start2, end2) => {
      return start1 < end2 && start2 < end1;
    };

    // Check conflicts with existing appointments
    appointmentsForDate.forEach(appt => {
      const appointmentTime = appt.appointment_start || appt.scheduled_time;
      const apptStart = convertUTCToLocal(appointmentTime);
      const apptDuration = appt.duration || 60; // Get duration from appointment if available
      const apptEnd = new Date(apptStart.getTime() + apptDuration * 60000);

      if (timeRangesOverlap(selectedStart, selectedEnd, apptStart, apptEnd)) {
        const apptModalities = appt.modalities.filter(m => m !== 'Lab');
        const conflictingModalities = packageModalities.filter(m => apptModalities.includes(m));

        if (conflictingModalities.length > 0) {
          conflicts.push({
            patient: `${appt.first_name} ${appt.last_name}`,
            time: apptStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
            modalities: conflictingModalities.join(', '),
            ris_patients_id: appt.ris_patients_id,
            reason: 'Modality conflict'
          });
        }
      }
    });

    // Check conflicts with other packages scheduled for the same person
    Object.keys(packageAppointments).forEach(otherPkgId => {
      if (otherPkgId === packageId) return;

      const otherAppt = packageAppointments[otherPkgId];
      if (!otherAppt || !otherAppt.date) return;

      const otherPkg = SCREENING_PACKAGES.find(p => p.id === otherPkgId);
      if (!otherPkg) return;

      const otherStart = new Date(otherAppt.date);
      const otherDuration = otherPkg.duration || 60;
      const otherEnd = new Date(otherStart.getTime() + otherDuration * 60000);

      // Check if time ranges overlap
      if (timeRangesOverlap(selectedStart, selectedEnd, otherStart, otherEnd)) {
        const otherModalities = getPackageModalities(otherPkgId);
        const conflictingModalities = packageModalities.filter(m => otherModalities.includes(m));

        // Conflict exists if either:
        // 1. There are shared modalities, OR
        // 2. Same person can't do multiple packages simultaneously (even without shared modalities)
        const hasModalityConflict = conflictingModalities.length > 0;
        const hasTimingConflict = true; // Always true when time ranges overlap for same person

        conflicts.push({
          patient: 'Self (Different Package)',
          time: otherStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
          modalities: hasModalityConflict ? conflictingModalities.join(', ') : 'N/A',
          exam: otherPkg.name,
          reason: hasModalityConflict 
            ? 'Modality conflict' 
            : 'Cannot schedule multiple packages simultaneously',
          duration: `${otherDuration} min`
        });
      }
    });

    return conflicts;
};

  const handlePackageTimeChange = async (packageId, date) => {
    // Track which package we're scheduling for booked slots
    setCurrentPackageForSlots(packageId);
  
    if (date && (!selectedDate || date.toDateString() !== selectedDate.toDateString())) {
      setSelectedDate(date);
      await fetchAppointmentsForDate(date);
    }

    // Create the updated appointments object with the new date
    const updatedAppointments = {
      ...packageAppointments,
      [packageId]: {
        date,
        conflicts: [] // Temporarily set to empty, will recalculate below
      }
    };

    // Now recalculate conflicts for ALL packages using the updated state
    Object.keys(updatedAppointments).forEach(pkgId => {
      const appt = updatedAppointments[pkgId];
      if (appt && appt.date) {
        // Use a modified version of checkModalityConflicts that accepts the updated state
        const newConflicts = checkModalityConflictsWithState(
          pkgId, 
          new Date(appt.date),
          updatedAppointments
        );
        
        updatedAppointments[pkgId] = {
          ...appt,
          conflicts: newConflicts
        };
      }
    });

    // Single state update with all recalculated conflicts
    setPackageAppointments(updatedAppointments);
  };

  const recalculateAllConflicts = () => {
    const updatedAppointments = { ...packageAppointments };
    let hasChanges = false;

    Object.keys(updatedAppointments).forEach(pkgId => {
      const appt = updatedAppointments[pkgId];
      if (appt && appt.date) {
        // Use the state-aware version
        const newConflicts = checkModalityConflictsWithState(pkgId, new Date(appt.date), updatedAppointments);
        
        const conflictsChanged = JSON.stringify(appt.conflicts) !== JSON.stringify(newConflicts);
        
        if (conflictsChanged) {
          updatedAppointments[pkgId] = {
            ...appt,
            conflicts: newConflicts
          };
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setPackageAppointments(updatedAppointments);
    }
  };

  const createIndividualExamOrders = async (appointmentId, packageId, patientData) => {
    const package_info = SCREENING_PACKAGES.find(pkg => pkg.id === packageId);
    if (!package_info || !package_info.exams) return;

    const exams = package_info.exams.map(exam => ({
      exam_name: exam.name,
      modality: exam.modality,
      package_name: package_info.name,
      appointment_id: appointmentId,
      code: exam.code,
      type: exam.type,
      region: exam.region,
      body_part: exam.body_part
    }));

    try {
      const response = await fetch(`${BASE_URL}/orders/createExamOrders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          exams,
          patient_id: patientData.id || null,
          first_name: patientData.first_name,
          last_name: patientData.last_name,
          dob: patientData.dob,
          user,
          type: currentUser.type
        })
      });

      if (!response.ok) {
        logger.error('Failed to create individual exam orders for package:', packageId);
      }
    } catch (error) {
      logger.error('Error creating exam orders:', error);
    }
  };

  const scheduleScreening = async () => {
    const selectedScreenings = Object.keys(selectedPackages).filter(key => selectedPackages[key]);

    if (selectedScreenings.length === 0) {
      toast.error('Please select at least one screening package');
      return;
    }

    const patientData = selectedPatient || patientInfo;
    if (!patientData.first_name || !patientData.last_name || !patientData.dob) {
      toast.error('Please fill in all patient information');
      return;
    }

    const missingTimes = selectedScreenings.filter(pkgId => {
      return !packageAppointments[pkgId]?.date;
    });

    if (missingTimes.length > 0) {
      const missingNames = missingTimes.map(pkgId => {
        const pkg = SCREENING_PACKAGES.find(p => p.id === pkgId);
        return pkg?.name;
      }).join(', ');
      toast.error(`Please select appointment times for: ${missingNames}`);
      return;
    }

    const hasConflicts = selectedScreenings.some(pkgId => {
      const pkg = SCREENING_PACKAGES.find(p => p.id === pkgId);
      const packageModalities = getPackageModalities(pkgId);

      if (packageModalities.length === 0) return false;

      const appt = packageAppointments[pkgId];
      return appt?.conflicts && appt.conflicts.length > 0;
    });

    if (hasConflicts) {
      toast.error('Please resolve modality conflicts before proceeding');
      return;
    }

    try {
      let createdPatientId = selectedPatient?.ris_patients_id;

      for (const packageId of selectedScreenings) {
        const pkg = SCREENING_PACKAGES.find(p => p.id === packageId);
        const apptTime = packageAppointments[packageId]?.date;

        let appointmentStart, appointmentEnd;
        
        if (apptTime) {
          appointmentStart = convertLocalToUTC(apptTime);
          
          if (pkg.duration > 0) {
            appointmentEnd = new Date(new Date(apptTime).getTime() + pkg.duration * 60000).toISOString();
          } else {
            appointmentEnd = appointmentStart;
          }
        }

        const appointmentData = {
          visit_type: 'Screening',
          exam: pkg.name,
          email: patientData.email,
          first_name: patientData.first_name,
          last_name: patientData.last_name,
          dob: patientData.dob,
          phone_number: patientData.phone_number,
          sex: patientData.sex,
          appointment_start: appointmentStart,
          appointment_end: appointmentEnd,
          user,
          type: currentUser.type
        };

        let response;
        
        if (!createdPatientId) {
          response = await api.scheduleAppointment(appointmentData, user, currentUser.type);
          
          if (response.status === 201) {
            const responseData = await response.json();
            createdPatientId = responseData.ris_patients_id;
            
            await createIndividualExamOrders(responseData.id, packageId, { 
              ...patientData, 
              id: createdPatientId 
            });
          }
        } else {
          appointmentData.ris_patients_id = createdPatientId;
          response = await api.scheduleReturningPatient(appointmentData, user, currentUser.type);
          
          if (response.status === 201) {
            const responseData = await response.json();
            
            await createIndividualExamOrders(responseData.id, packageId, { 
              ...patientData, 
              id: createdPatientId 
            });
          }
        }

        if (response.status !== 201) {
          toast.error(`Failed to schedule ${pkg.name}`);
          return;
        }
      }

      toast.success('All screening appointments scheduled successfully!');

      setSelectedPackages({});
      setPackageAppointments({});
      setPatientInfo({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        dob: '',
        sex: ''
      });
      setSelectedPatient(null);
      setIsNewPatient(false);
      setSearchQuery('');
      setPatients([]);

      if (onScheduleComplete) onScheduleComplete();
    } catch (error) {
      logger.error('Error scheduling screening:', error);
      toast.error('Error scheduling screening appointment');
    }
  };

  const fetchBookedSlotsForPackage = async (packageId, selectedDate) => {
    try {
      const packageModalities = getPackageModalities(packageId);
      
      // If no modalities (Lab-only package), no need to fetch
      if (packageModalities.length === 0) {
        setBookedSlots([]);
        return;
      }

      const dateStr = selectedDate 
        ? selectedDate.toISOString().split('T')[0] 
        : new Date().toISOString().split('T')[0];
      
      // Join modalities with comma for the query parameter
      const modalitiesParam = packageModalities.join(',');
      
      const response = await fetch(
        `${BASE_URL}/scheduling/bookedSlotsScreening?date=${dateStr}&modalities=${modalitiesParam}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const excludedTimes = [];
          
          data.forEach((slot) => {
            // Use helper function to convert UTC to local
            const startTime = convertUTCToLocal(slot.appointment_start);
            const endTime = convertUTCToLocal(slot.appointment_end);
            
            let currentTime = new Date(startTime);
            while (currentTime < endTime) {
              excludedTimes.push(new Date(currentTime));
              // 60-minute intervals for screening packages
              currentTime = new Date(currentTime.getTime() + 60 * 60000);
            }
            
            // excludedTimes.push(new Date(endTime));
          });
          
          setBookedSlots(excludedTimes);
        } else {
          setBookedSlots([]);
        }
      }
    } catch (error) {
      logger.error("Error fetching booked slots for screening package", error);
      toast.error("Error fetching available slots");
    }
  };


  // Fetch booked slots when a package date is being selected
  useEffect(() => {
    if (currentPackageForSlots && selectedDate) {
      fetchBookedSlotsForPackage(currentPackageForSlots, selectedDate);
    }
  }, [currentPackageForSlots, selectedDate]);

  useEffect(() => {
    if (Object.keys(packageAppointments).length > 0) {
      recalculateAllConflicts();
    }
  }, [appointmentsForDate]);

  return (
    <div className="screening-section">
      <h3 className="section-title">Screening Packages</h3>
      
      {/* Patient Search/Selection */}
      <div className="patient-search-section">
        <div className="patient-type-toggle">
          <button 
            className={!isNewPatient ? 'active' : ''}
            onClick={() => {
              setIsNewPatient(false);
              setSelectedPatient(null);
            }}
          >
            Returning Patient
          </button>
          <button 
            className={isNewPatient ? 'active' : ''}
            onClick={() => {
              setIsNewPatient(true);
              setSelectedPatient(null);
              setPatients([]);
              setSearchQuery('');
            }}
          >
            New Patient
          </button>
        </div>

        {!isNewPatient ? (
          <div className="search-container">
            <h4>Search for Patient</h4>
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by name, DOB, or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                className="modern-search-input"
              />
              <button 
                onClick={() => handleSearch(searchQuery)} 
                className="search-btn"
              >
                <Search />
              </button>
            </div>
            {patients.length > 0 && (
              <div className="patient-results">
                {patients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => {
                      setSelectedPatient(patient);
                      setPatients([]);
                    }}
                    className={`patient-result-card ${selectedPatient?.ris_patients_id === patient.ris_patients_id ? 'selected-patient' : ''}`}
                  >
                    <div className="patient-info">
                      <div className="patient-name">{patient.first_name} {patient.last_name}</div>
                      <div className="patient-details">
                        {patient.dob} • {patient.phone_number}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedPatient && (
              <div className="selected-patient-banner">
                <strong>Selected Patient:</strong> {selectedPatient.first_name} {selectedPatient.last_name} | {selectedPatient.dob}
              </div>
            )}
          </div>
        ) : (
          <div className="new-patient-form">
            <h4>New Patient Information</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>First Name *</label>
                <input
                  type="text"
                  className="modern-input"
                  placeholder="First Name"
                  value={patientInfo.first_name}
                  onChange={(e) => setPatientInfo({...patientInfo, first_name: e.target.value})}
                />
              </div>
              <div className="form-field">
                <label>Last Name *</label>
                <input
                  type="text"
                  className="modern-input"
                  placeholder="Last Name"
                  value={patientInfo.last_name}
                  onChange={(e) => setPatientInfo({...patientInfo, last_name: e.target.value})}
                />
              </div>
              <div className="form-field">
                <label>Email *</label>
                <input
                  type="email"
                  className="modern-input"
                  placeholder="patient@email.com"
                  value={patientInfo.email}
                  onChange={(e) => setPatientInfo({...patientInfo, email: e.target.value})}
                />
              </div>
              <div className="form-field">
                <label>Phone Number *</label>
                <InputMask
                  mask="(XXX) XXX-XXXX"
                  replacement={{ X: /\d/ }}
                  className="modern-input"
                  placeholder="(555) 555-5555"
                  value={patientInfo.phone_number}
                  onChange={(e) => setPatientInfo({...patientInfo, phone_number: e.target.value})}
                />
              </div>
              <div className="form-field">
                <label>Date of Birth *</label>
                <input
                  type="date"
                  className="modern-input"
                  value={patientInfo.dob}
                  onChange={(e) => setPatientInfo({...patientInfo, dob: e.target.value})}
                />
              </div>
              <div className="form-field">
                <label>Sex *</label>
                <select
                  className="modern-select"
                  value={patientInfo.sex}
                  onChange={(e) => setPatientInfo({...patientInfo, sex: e.target.value})}
                >
                  <option value="">Select Sex</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Screening Packages Grid */}
      <div className="packages-grid">
        {SCREENING_PACKAGES.map(pkg => (
          <div 
            key={pkg.id}
            className={`package-card ${selectedPackages[pkg.id] ? 'selected' : ''}`}
            onClick={() => handleTogglePackage(pkg.id)}
          >
            <div className="package-icon">{pkg.icon}</div>
            <h4 className="package-name">{pkg.name}</h4>
            <p className="package-price">${pkg.price}</p>
            <p className="package-description">{pkg.description}</p>
            <div className="package-exams">
              {pkg.exams.map((exam, idx) => (
                <div key={idx} className="exam-item">• {exam.name} ({exam.modality})</div>
              ))}
            </div>
            <div className="package-checkbox">
              {selectedPackages[pkg.id] ? '✓' : '+'}
            </div>
          </div>
        ))}
      </div>

      {/* Show appointment scheduling when packages are selected and patient is chosen */}
      {Object.values(selectedPackages).some(v => v) && (selectedPatient || isNewPatient) && (
        <div className="screening-booking-form">
          <h4 className="form-title">Schedule Appointment Times</h4>
          <p className="scheduling-info">Each package requires a separate appointment time. Please select times for each package below.</p>

          {Object.keys(selectedPackages)
            .filter(pkgId => selectedPackages[pkgId])
            .map(packageId => {
              const pkg = SCREENING_PACKAGES.find(p => p.id === packageId);
              const apptData = packageAppointments[packageId];
              const packageModalities = getPackageModalities(packageId);
              const isLabsOnly = packageModalities.length === 0;

              return (
                <div key={packageId} className={`package-time-slot ${isLabsOnly ? 'labs-package' : ''}`}>
                  <div className="package-time-header">
                    <span className="package-icon">{pkg.icon}</span>
                    <span className="package-name">{pkg.name}</span>
                    {!isLabsOnly && pkg.duration && (
                      <span className="package-duration">({pkg.duration >= 60 ? `${pkg.duration / 60 === 1 ? '1 hour' : `${pkg.duration / 60} hours`}` : `${pkg.duration} minutes`})</span>
                    )}
                    {isLabsOnly && (
                      <span className="labs-note">(No modality conflicts - flexible scheduling)</span>
                    )}
                  </div>

                  <div className="form-field">
                    <span className="schedule-date-legend">Greyed-out dates are unavailable — clinic closed or no staff available for this package's exams.</span>
                    <DatePicker
                      selected={apptData?.date}
                      onChange={(date) => handlePackageTimeChange(packageId, date)}
                      onMonthChange={fetchUnavailableDates}
                      showTimeSelect
                      minTime={APPOINTMENT_MIN_TIME}
                      maxTime={APPOINTMENT_MAX_TIME}
                      timeIntervals={pkg.duration || 60}
                      dateFormat="MMM d, yyyy h:mm aa"
                      className="modern-datepicker"
                      minDate={new Date()}
                      placeholderText={`Select time for ${pkg.name}`}
                      excludeTimes={bookedSlots} // Add this to grey out booked times
                      onCalendarOpen={() => setCurrentPackageForSlots(packageId)} // Fetch slots when calendar opens
                      filterDate={(date) => !isPackageDateUnavailable(packageId, date)}
                      dayClassName={(date) => getPackageDateUnavailableReason(packageId, date) ? 'schedule-day-unavailable' : undefined}
                      renderDayContents={(dayOfMonth, date) => {
                        const reason = date ? getPackageDateUnavailableReason(packageId, date) : null;
                        return <span title={reason || undefined}>{dayOfMonth}</span>;
                      }}
                    />
                    {/* <DatePicker
                      selected={apptData?.date}
                      onChange={(date) => handlePackageTimeChange(packageId, date)}
                      showTimeSelect
                      timeIntervals={60}
                      dateFormat="MMM d, yyyy h:mm aa"
                      className="modern-datepicker"
                      minDate={new Date()}
                      placeholderText={`Select time for ${pkg.name}`}
                      filterTime={(time) => {
                      // Check if this time conflicts with any booked slot
                      return !bookedSlots.some(slot => {
                        const selectedDateStr = (form.appointmentDate || form.currentDate)?.toDateString();
                        const slotDateStr = slot.toDateString();
                        
                        // Only compare times on the same day
                        if (selectedDateStr !== slotDateStr) return false;
                        
                        // Compare hours and minutes
                        return time.getHours() === slot.getHours() && 
                              time.getMinutes() === slot.getMinutes();
                      });
                    }}
                    /> */}
                  </div>

                  {/* {apptData?.conflicts && apptData.conflicts.length > 0 && (
                    <div className="conflict-warning">
                      <strong>⚠️ Modality Conflicts Detected:</strong>
                      <ul>
                        {apptData.conflicts.map((conflict, idx) => (
                          <li key={idx}>
                            {conflict.patient} at {conflict.time} - Conflicting modalities: {conflict.modalities}
                            {conflict.exam && ` (${conflict.exam})`}
                          </li>
                        ))}
                      </ul>
                      <p className="conflict-note">These appointments require the same equipment at the same time. Please select a different time.</p>
                    </div>
                  )} */}

                  {apptData?.conflicts && apptData.conflicts.length > 0 && (
                    <div className="conflict-warning">
                      <strong>⚠️ Scheduling Conflicts Detected:</strong>
                      <ul>
                        {apptData.conflicts.map((conflict, idx) => (
                          <li key={idx}>
                            <div>
                              <strong>{conflict.patient}</strong> at {conflict.time}
                              {conflict.exam && ` - ${conflict.exam}`}
                              {conflict.duration && ` (${conflict.duration})`}
                            </div>
                            <div className="conflict-reason">
                              {conflict.reason === 'Modality conflict' 
                                ? `⚠️ Requires same equipment: ${conflict.modalities}`
                                : '⚠️ Time slots overlap - patient cannot attend multiple appointments simultaneously'}
                            </div>
                          </li>
                        ))}
                      </ul>
                      <p className="conflict-note">Please select a different time to avoid scheduling conflicts.</p>
                    </div>
                  )}

                  {apptData?.date && (!apptData.conflicts || apptData.conflicts.length === 0) && (
                    <div className="time-confirmed">
                      ✓ {isLabsOnly ? 'Time selected' : 'No modality conflicts - Time available'}
                    </div>
                  )}
                </div>
              );
            })}

          <div className="screening-actions">
            <button onClick={scheduleScreening} className="primary-btn">
              Schedule All Selected Packages
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Reschedule Modal
function RescheduleModal({ appointment, onClose, onReschedule, bookedSlots, newDate, onDateChange }) {
  const handleReschedule = () => {
    if (!newDate) {
      toast.error('Please select a new date and time');
      return;
    }
    onReschedule(appointment, newDate);
  };

  // Determine time intervals based on visit type
  const getTimeIntervals = () => {
    if (appointment.visit_type === 'Radiology') {
      // Ultrasound books on-the-hour, 60 minute slots.
      if (appointment.exam === 'US') return 60;
      // Contrast CT blocks a 60 minute slot instead of the standard 30 — match that
      // granularity when rescheduling one (reschedule preserves the original duration).
      if (appointment.exam === 'CT' && appointment.appointment_start && appointment.appointment_end) {
        const existingMinutes = (new Date(appointment.appointment_end) - new Date(appointment.appointment_start)) / 60000;
        if (existingMinutes >= 60) return 60;
      }
      return 30;
    }
    if (appointment.visit_type === 'Screening') return 60;
    return 10; // Default for Lab, Clinic, etc.
  };

  // Keyed by 'YYYY-MM-DD' -> { clinicClosed, blockedModalities, allLabsBlocked,
  // blockedSpecimens } — same shape/source as the main calendar's monthAvailability,
  // so rescheduling can't be dragged onto a clinic-closed or equipment/staff-blocked day.
  const [unavailableDates, setUnavailableDates] = useState({});

  const fetchUnavailableDates = async (monthDate) => {
    try {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const [closureRes, modalityRes, labRes] = await Promise.all([
        fetch(`${BASE_URL}/scheduling/clinicClosures?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/modalityAvailability?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/labAvailability?startDate=${startDate}&endDate=${endDate}`),
      ]);
      const closures = closureRes.ok ? await closureRes.json() : [];
      const modalityData = modalityRes.ok ? await modalityRes.json() : {};
      const labData = labRes.ok ? await labRes.json() : {};

      setUnavailableDates(prev => {
        const merged = { ...prev };
        closures.forEach(c => {
          const d = c.blocked_date.split('T')[0];
          merged[d] = { ...(merged[d] || {}), clinicClosed: true };
        });
        Object.keys(modalityData).forEach(d => {
          merged[d] = { ...(merged[d] || {}), blockedModalities: modalityData[d].blockedModalities };
        });
        Object.keys(labData).forEach(d => {
          merged[d] = {
            ...(merged[d] || {}),
            allLabsBlocked: labData[d].allBlocked,
            blockedSpecimens: labData[d].blockedSpecimens,
          };
        });
        return merged;
      });
    } catch (error) {
      logger.error('Error fetching unavailable dates', error);
    }
  };

  useEffect(() => {
    fetchUnavailableDates(newDate || new Date());
  }, []);

  const getDateUnavailableReason = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const info = unavailableDates[dateStr];
    if (!info) return null;
    if (info.clinicClosed) return 'Clinic closed';
    if (appointment.visit_type === 'Radiology' && info.blockedModalities?.includes(appointment.exam)) {
      return `No rad tech available to run ${appointment.exam}`;
    }
    if (appointment.visit_type === 'Lab') {
      if (info.allLabsBlocked) return 'No lab-capable staff available';
      if (info.blockedSpecimens?.includes(appointment.exam)) return `No staff available to run ${appointment.exam} labs`;
    }
    return null;
  };
  const isDateUnavailable = (date) => getDateUnavailableReason(date) !== null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Reschedule Appointment</h3>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>
        <div className="modal-body">
          <div className="current-appointment-info">
            <p><strong>{appointment.first_name} {appointment.last_name}</strong></p>
            <p>{appointment.visit_type} - {appointment.exam || 'N/A'}</p>
            {/* <p>Current: {formatDateOfBirth(convertUTCToCentral(appointment.scheduled_time))} at {formatTimePlusFiveHours(appointment.scheduled_time)}</p> */}
            <p>Current: {formatUTCForDisplay(appointment.appointment_start) || formatUTCForDisplay(appointment.scheduled_time)}</p>
          </div>
          <div className="form-field">
            <label>Select New Date & Time</label>
            <span className="schedule-date-legend">Greyed-out dates are unavailable — clinic closed or no staff/equipment to perform this exam.</span>
            <DatePicker
              selected={newDate}
              onChange={onDateChange}
              onMonthChange={fetchUnavailableDates}
              showTimeSelect
              minTime={APPOINTMENT_MIN_TIME}
              maxTime={APPOINTMENT_MAX_TIME}
              timeIntervals={getTimeIntervals()}
              dateFormat="MMMM d, yyyy h:mm aa"
              className="modern-datepicker"
              minDate={new Date()}
              excludeTimes={bookedSlots}
              filterDate={(date) => !isDateUnavailable(date)}
              dayClassName={(date) => getDateUnavailableReason(date) ? 'schedule-day-unavailable' : undefined}
              renderDayContents={(dayOfMonth, date) => {
                const reason = date ? getDateUnavailableReason(date) : null;
                return <span title={reason || undefined}>{dayOfMonth}</span>;
              }}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="secondary-btn">Cancel</button>
          <button onClick={handleReschedule} className="primary-btn">Confirm Reschedule</button>
        </div>
      </div>
    </div>
  );
}

// Derives an appointment's own lifecycle status for display from the schedule
// row's fields (status / check_in_time / location) — NOT the linked order's
// status. This way the card reflects where the patient actually is: scheduled,
// checked in, or checked out, regardless of how the order is progressing.
function getAppointmentDisplayStatus(appt) {
  const raw = (appt.status || '').toLowerCase();
  const location = (appt.location || '').toLowerCase();

  if (raw === 'cancelled' || raw === 'canceled') return { key: 'cancelled', label: 'Cancelled' };
  if (raw === 'no-show' || raw === 'noshow') return { key: 'no-show', label: 'No-Show' };

  // 'Complete' is the terminal in-facility location, set when the patient checks out.
  if (location === 'complete' || raw === 'complete' || raw === 'completed') {
    return { key: 'checked-out', label: 'Checked Out' };
  }

  // The patient is on-site once checked in: a check-in timestamp exists, the raw
  // status is 'arrived', or they've been moved past the initial 'Scheduled' location.
  const onSite = appt.check_in_time || raw === 'arrived' || (location && location !== 'scheduled');
  if (onSite) return { key: 'checked-in', label: 'Checked In' };

  return { key: 'scheduled', label: 'Scheduled' };
}

// WeekView Component
function WeekView({ currentWeek, setCurrentWeek }) {
  const [weekAppointments, setWeekAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const { currentUser, setCurrentPatient } = useContext(GlobalStateContext);
  const user = getUserId(currentUser);
  const navigate = useNavigate();

  const handleMouseEnter = (e, appt, color) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipWidth = 268;
    const x = rect.right + 12 + tooltipWidth > window.innerWidth
      ? rect.left - tooltipWidth - 12
      : rect.right + 12;
    setTooltip({ appt, x, y: rect.top, color });
  };

  const handleAppointmentClick = (appt) => {
    if (appt.ris_patients_id) {
      setCurrentPatient({ ris_patients_id: appt.ris_patients_id });
    }
    navigate('/orders', {
      state: {
        orderId: appt.order_id,
        orderType: appt.order_type === 'lab' ? 'Lab' : 'Radiology',
      }
    });
  };

  // Define modalities and their colors
  const MODALITIES = [
    { name: 'US', color: '#EF4444' },        // Red
    { name: 'CT', color: '#F59E0B' },        // Amber
    { name: 'DEXA', color: '#ecdb48' },      // Yellow
    { name: 'Lab', color: '#10b951' },        // Green
    { name: 'X-Ray', color: '#3B82F6' },    // Blue
    { name: 'MRI', color: '#8B5CF6' }       // Purple
  ];

  // Generate time slots from 9am to 6pm (30-minute intervals)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 18 && minute > 0) break; // Stop at 6:00pm
        const time = new Date();
        time.setHours(hour, minute, 0, 0);
        slots.push(time);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Get start of the week (Monday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    return new Date(d.setDate(diff));
  };

  // Get days of the week
  const getWeekDays = () => {
    const weekStart = getWeekStart(currentWeek);
    const days = [];
    for (let i = 0; i < 5; i++) { // Monday to Friday
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const weekDays = getWeekDays();

  useEffect(() => {
    fetchWeekAppointments();
  }, [currentWeek]);

  const fetchWeekAppointments = async () => {
    try {
      const weekStart = getWeekStart(currentWeek);
      const startDateStr = weekStart.toISOString().split('T')[0];

      logger.log('Fetching week appointments for:', startDateStr);

      const response = await fetch(
        `${BASE_URL}/scheduling/weekScheduleView?startDate=${startDateStr}&user=${user}&type=${currentUser.type}`
      );

      if (response.ok) {
        const data = await response.json();
        logger.log('Received appointments:', data.length, data);
        setWeekAppointments(data);
      } else {
        logger.error('Failed to fetch appointments:', response.status);
        setWeekAppointments([]);
      }
    } catch (error) {
      logger.error('Error fetching weekly appointments:', error);
      setWeekAppointments([]);
    }
  };

  // Get appointments for a specific time slot, day, and modality
  const getAppointmentsForSlot = (timeSlot, day, modality) => {
    const matches = weekAppointments.filter(appt => {
      // Check if appointment matches the modality
      // For labs, modality is null in the data, so check order_type
      const appointmentModality = appt.order_type === 'lab' ? 'Lab' : appt.modality;
      if (appointmentModality !== modality) return false;

      // Parse appointment times - use event_time from the new backend structure
      const apptStart = new Date(appt.event_time);
      // Calculate end time - default to 30 minutes if not specified
      const apptEnd = appt.appointment_end ? new Date(appt.appointment_end) : new Date(apptStart.getTime() + 30 * 60000);

      // Check if appointment is on the same day
      const apptDay = apptStart.toDateString();
      const checkDay = day.toDateString();
      if (apptDay !== checkDay) return false;

      // Create slot time for comparison
      const slotTime = new Date(day);
      slotTime.setHours(timeSlot.getHours(), timeSlot.getMinutes(), 0, 0);

      // Check if the time slot falls within the appointment window
      // An appointment should appear in the first slot it occupies
      const slotEnd = new Date(slotTime.getTime() + 30 * 60000); // 30 minutes later

      // Show appointment if slot start is within appointment time or if appointment starts within slot
      return (apptStart <= slotTime && slotTime < apptEnd) ||
             (slotTime <= apptStart && apptStart < slotEnd);
    });

    return matches;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDayHeader = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const nextWeek = () => {
    const next = new Date(currentWeek);
    next.setDate(next.getDate() + 7);
    setCurrentWeek(next);
  };

  const prevWeek = () => {
    const prev = new Date(currentWeek);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeek(prev);
  };

  const goToToday = () => {
    setCurrentWeek(new Date());
  };

  return (
    <div className="week-view-container">
      
        <div className="week-nav-controls">
          <button onClick={prevWeek} className="calendar-nav-btn">‹</button>
          <button onClick={goToToday} className="today-btn">Today</button>
          <button onClick={nextWeek} className="calendar-nav-btn">›</button>
        </div>
        <div className="week-view-header">
          <h2 className="week-title">
            Week of {getWeekStart(currentWeek).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </h2>
        </div>

      {/* Legend */}
      <div className="modality-legend">
        {MODALITIES.map(mod => (
          <div key={mod.name} className="legend-item">
            <div className="legend-color" style={{ backgroundColor: mod.color }}></div>
            <span>{mod.name}</span>
          </div>
        ))}
      </div>

      {tooltip && (() => {
        const { appt, x, y, color } = tooltip;
        const tStart = new Date(appt.event_time);
        const tEnd = appt.appointment_end ? new Date(appt.appointment_end) : new Date(tStart.getTime() + 30 * 60000);
        const tExam = appt.exam_ordered || appt.exam_name || appt.exam || appt.body_part || 'Exam';
        const tModality = appt.order_type === 'lab' ? 'Lab' : appt.modality;
        const tName = (appt.first_name && appt.last_name) ? `${appt.first_name} ${appt.last_name}` : 'Patient';
        return (
          <div className="appt-hover-card" style={{ top: y, left: x }}>
            <div className="appt-hover-card-header" style={{ backgroundColor: color }}>
              <span
                className="appt-hover-patient-name"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentPatient({ ris_patients_id: appt.ris_patients_id });
                  navigate('/patientAbout');
                }}
              >
                {tName}
              </span>
              {(() => {
                const apptStatus = getAppointmentDisplayStatus(appt);
                return (
                  <span className={`appt-hover-status status-badge ${apptStatus.key}`}>
                    {apptStatus.label}
                  </span>
                );
              })()}
            </div>
            <div className="appt-hover-card-body">
              {appt.phone_number && (
                <div className="appt-hover-row">
                  <span className="appt-hover-label">Phone</span>
                  <span className="appt-hover-value">{appt.phone_number}</span>
                </div>
              )}
              {appt.dob && (
                <div className="appt-hover-row">
                  <span className="appt-hover-label">DOB</span>
                  <span className="appt-hover-value">{appt.dob}</span>
                </div>
              )}
              <div className="appt-hover-row">
                <span className="appt-hover-label">Exam</span>
                <span className="appt-hover-value">{tExam}</span>
              </div>
              <div className="appt-hover-row">
                <span className="appt-hover-label">Modality</span>
                <span className="appt-hover-value">{tModality}</span>
              </div>
              <div className="appt-hover-row">
                <span className="appt-hover-label">Time</span>
                <span className="appt-hover-value">{formatTime(tStart)} – {formatTime(tEnd)}</span>
              </div>
              <div className="appt-hover-row">
                <span className="appt-hover-label">Order ID</span>
                <span className="appt-hover-value">#{appt.order_id}</span>
              </div>
              {appt.clinical_history && (
                <div className="appt-hover-row appt-hover-row--full">
                  <span className="appt-hover-label">Clinical History</span>
                  <span className="appt-hover-value">{appt.clinical_history}</span>
                </div>
              )}
            </div>
            <div className="appt-hover-card-footer">Click appointment to view order</div>
          </div>
        );
      })()}

      <div className="week-view-scroll">
        <table className="week-schedule-table">
          <thead>
            <tr>
              <th className="time-column">Time</th>
              {weekDays.map((day, idx) => (
                <th key={idx} colSpan={6} className="day-header">
                  {formatDayHeader(day)}
                </th>
              ))}
            </tr>
            <tr>
              <th className="time-column"></th>
              {weekDays.map((day, dayIdx) => (
                MODALITIES.map((mod, modIdx) => (
                  <th key={`${dayIdx}-${modIdx}`} className="modality-header" style={{ backgroundColor: mod.color + '20' }}>
                    {mod.name}
                  </th>
                ))
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((timeSlot, timeIdx) => (
              <tr key={timeIdx} className="time-row">
                <td className="time-cell">{formatTime(timeSlot)}</td>
                {weekDays.map((day, dayIdx) => (
                  MODALITIES.map((modality, modIdx) => {
                    const appointments = getAppointmentsForSlot(timeSlot, day, modality.name);
                    return (
                      <td
                        key={`${dayIdx}-${modIdx}`}
                        className={`schedule-cell ${appointments.length > 0 ? 'has-appointment' : ''}`}
                        style={{
                          borderLeft: modIdx === 0 ? '2px solid #ddd' : undefined
                        }}
                      >
                        {appointments.map((appt, apptIdx) => {
                          const apptStart = new Date(appt.event_time);
                          const apptEnd = appt.appointment_end ? new Date(appt.appointment_end) : new Date(apptStart.getTime() + 30 * 60000);
                          const startTime = formatTime(apptStart);
                          const endTime = formatTime(apptEnd);
                          const examName = appt.exam_ordered || appt.exam_name || appt.exam || appt.body_part || 'Exam';
                          const patientName = (appt.first_name && appt.last_name) ? `${appt.first_name} ${appt.last_name}` : 'Patient';
                          const displayModality = appt.order_type === 'lab' ? 'Lab' : appt.modality;

                          return (
                            <div
                              key={apptIdx}
                              className="appointment-block"
                              style={{
                                backgroundColor: modality.color,
                                color: '#fff',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => handleMouseEnter(e, appt, modality.color)}
                              onMouseLeave={() => setTooltip(null)}
                              onClick={() => handleAppointmentClick(appt)}
                            >
                              <div className="appt-patient">
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentPatient({ ris_patients_id: appt.ris_patients_id });
                                    navigate('/patientAbout');
                                  }}
                                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                >
                                  {patientName}
                                </span>
                              </div>
                              <div className="appt-exam">{examName}</div>
                              <div className="appt-time">
                                {startTime} - {endTime}
                              </div>
                            </div>
                          );
                        })}
                      </td>
                    );
                  })
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const CONSULTATION_STATUS_LABELS = {
  pending:   { label: 'Awaiting Consultation', color: '#6b7280' },
  completed: { label: 'Consultation Completed', color: '#16a34a' },
  scheduled: { label: 'Follow-up Appointment Needed', color: '#f59e0b' },
  booked:    { label: 'Follow-up Appointment Scheduled', color: '#2563eb' },
  declined:  { label: 'Patient Declined', color: '#b91c1c' },
};

// Appointments List Component with UTC support
function AppointmentsList({ appointments, onReschedule, onCancel, selectedDate, onRefresh }) {
  const [filter, setFilter] = useState('all');
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [consultationUpdating, setConsultationUpdating] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [editAppt, setEditAppt] = useState(null);
  const [editBillInsurance, setEditBillInsurance] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [followUpAppt, setFollowUpAppt] = useState(null);
  const [followUpDate, setFollowUpDate] = useState(null);
  const [consultationBookedSlots, setConsultationBookedSlots] = useState([]);
  const { currentUser, setCurrentPatient } = useContext(GlobalStateContext);
  const user = getUserId(currentUser);
  const navigate = useNavigate();
  const currentDate = new Date();

  // Clinic-closed dates ('YYYY-MM-DD' set) for the follow-up consultation
  // picker below — consultations aren't Radiology/Lab, so only the
  // whole-clinic closure applies, not modality/lab equipment availability.
  const [closedDates, setClosedDates] = useState(new Set());
  const fetchClosedDates = async (monthDate) => {
    try {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const response = await fetch(`${BASE_URL}/scheduling/clinicClosures?startDate=${startDate}&endDate=${endDate}`);
      if (!response.ok) return;
      const data = await response.json();
      setClosedDates(prev => new Set([...prev, ...data.map(c => c.blocked_date.split('T')[0])]));
    } catch (error) {
      logger.error('Error fetching clinic closures', error);
    }
  };
  useEffect(() => {
    fetchClosedDates(new Date());
  }, []);
  const isClosedDate = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return closedDates.has(dateStr);
  };

  // Profile picture signed URLs for the appointments in view, keyed by
  // ris_patients_id. Fetched in one batch request so the list of cards doesn't
  // fan out into a request per patient. Signed URLs are short-lived, so we
  // re-fetch whenever the appointment set changes.
  const [avatars, setAvatars] = useState({});
  useEffect(() => {
    const ids = [...new Set(
      (appointments || [])
        .map(a => a.ris_patients_id)
        .filter(Boolean)
    )];
    if (ids.length === 0) {
      setAvatars({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/patients/profilePictures?ris_patients_ids=${ids.join(',')}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) {
            const map = {};
            data.forEach(p => { map[p.ris_patients_id] = p.url; });
            setAvatars(map);
          }
        }
      } catch (err) {
        logger.error('Error fetching appointment profile pictures:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [appointments]);

  // Navigate to the per-appointment payment workflow (HGIPayments), which builds
  // the bill for the selected appointment before handing off to the card screen.
  const goToPayment = (appt) => {
    setCurrentPatient({ ris_patients_id: appt.ris_patients_id });
    navigate('/hgiPayments', { state: { appointmentId: appt.id } });
  };

  // Check the patient out from the appointment card: marks the visit complete
  // (location='Complete') and offers a one-tap jump to collect payment.
  const handleCheckout = async (appt) => {
    setCheckingOut(appt.id);
    try {
      const response = await fetch(`${BASE_URL}/patients/patientCheckout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: appt.id, user, type: currentUser.type }),
      });

      if (response.ok) {
        toast.success(
          ({ closeToast }) => (
            <div className="checkout-toast">
              <span>{appt.first_name} {appt.last_name} checked out.</span>
              <button
                className="checkout-toast-btn"
                onClick={() => { closeToast(); goToPayment(appt); }}
              >
                Collect payment
              </button>
            </div>
          ),
          { autoClose: 8000 }
        );
        if (onRefresh) onRefresh();
      } else {
        toast.error('Failed to check out patient');
      }
    } catch (err) {
      logger.error('Error checking out patient:', err);
      toast.error('Error checking out patient');
    } finally {
      setCheckingOut(null);
    }
  };

  const handleConsultationStatus = async (appt, status) => {
    setConsultationUpdating(appt.id);
    try {
      const response = await fetch(`${BASE_URL}/scheduling/consultationStatus/${appt.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consultation_status: status, user, type: currentUser.type })
      });

      if (response.ok) {
        toast.success(`Consultation marked as: ${CONSULTATION_STATUS_LABELS[status]?.label || status}`);
        if (onRefresh) onRefresh();
      } else {
        toast.error('Failed to update consultation status');
      }
    } catch (err) {
      logger.error('Error updating consultation status:', err);
      toast.error('Error updating consultation status');
    } finally {
      setConsultationUpdating(null);
    }
  };

  const handleScheduleFollowUp = async () => {
    if (!followUpDate || !followUpAppt) return;
    setConsultationUpdating(followUpAppt.id);
    try {
      const response = await fetch(`${BASE_URL}/scheduling/scheduleConsultation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          screening_schedule_id: followUpAppt.id,
          appointment_start: convertLocalToUTC(followUpDate),
          user,
          type: currentUser.type
        })
      });

      if (response.ok) {
        toast.success('Follow-up consultation appointment scheduled!');
        setFollowUpAppt(null);
        setFollowUpDate(null);
        if (onRefresh) onRefresh();
      } else {
        toast.error('Failed to schedule follow-up consultation');
      }
    } catch (err) {
      logger.error('Error scheduling follow-up:', err);
      toast.error('Error scheduling follow-up consultation');
    } finally {
      setConsultationUpdating(null);
    }
  };

  const handleEditSave = async () => {
    if (!editAppt) return;
    setEditSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/scheduling/${editAppt.id}/billInsurance`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bill_insurance: editBillInsurance, user, type: currentUser.type }),
      });
      if (response.ok) {
        toast.success('Appointment updated successfully!');
        setEditAppt(null);
        if (onRefresh) onRefresh();
      } else {
        toast.error('Failed to update appointment');
      }
    } catch (err) {
      logger.error('Error updating appointment:', err);
      toast.error('Error updating appointment');
    } finally {
      setEditSaving(false);
    }
  };

  const fetchConsultationBookedSlots = async (date) => {
    try {
      const dateStr = date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      const response = await fetch(`${BASE_URL}/scheduling/bookedSlotsConsultation?date=${dateStr}`);
      if (response.ok) {
        const data = await response.json();
        const excluded = [];
        data.forEach((slot) => {
          const startTime = convertUTCToLocal(slot.appointment_start);
          const endTime = convertUTCToLocal(slot.appointment_end);
          let currentTime = new Date(startTime);
          while (currentTime < endTime) {
            excluded.push(new Date(currentTime));
            currentTime = new Date(currentTime.getTime() + 10 * 60000);
          }
        });
        setConsultationBookedSlots(excluded);
      }
    } catch (err) {
      logger.error('Error fetching consultation booked slots:', err);
    }
  };

  // Helper function to get modalities from a screening package
  const getScreeningPackageModalities = (examName) => {
    const pkg = SCREENING_PACKAGES.find(p => p.name === examName);
    if (!pkg) return [];

    return pkg.exams
      .map(exam => exam.modality)
      .filter(modality => modality !== 'Lab');
  };

  // Fetch booked slots based on appointment type and modality
  const fetchBookedSlots = async (appointment, selectedDate) => {
    try {
      let modalities = [];
      let timeInterval = 30; // Default

      // Determine modalities and time interval based on appointment type
      if (appointment.visit_type === 'Screening') {
        // For screening packages, get all modalities from the package
        modalities = getScreeningPackageModalities(appointment.exam);
        timeInterval = 60;
      } else if (appointment.visit_type === 'Radiology') {
        // For regular radiology appointments, use the single exam type
        modalities = [appointment.exam];
        timeInterval = 30;
      } else if (appointment.visit_type === 'Lab') {
        // Labs don't need slot blocking
        setBookedSlots([]);
        return;
      } else {
        // For other visit types (Clinic, Procedure, Physical)
        timeInterval = 10;
        // These might not have modality conflicts, so we can return empty
        setBookedSlots([]);
        return;
      }

      // If no modalities to check (e.g., Lab-only screening), return empty
      if (modalities.length === 0) {
        setBookedSlots([]);
        return;
      }

      const dateStr = selectedDate
        ? selectedDate.toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0];

      let response;

      if (appointment.visit_type === 'Screening') {
        // Use screening-specific endpoint
        const modalitiesParam = modalities.join(',');
        response = await fetch(
          `${BASE_URL}/scheduling/bookedSlotsScreening?date=${dateStr}&modalities=${modalitiesParam}`
        );
      } else {
        // Use worklist endpoint for regular appointments
        response = await fetch(
          `${BASE_URL}/scheduling/bookedSlotsWorklist?visit_type=${appointment.visit_type}&exam=${appointment.exam}&date=${dateStr}`
        );
      }

      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const excludedTimes = [];

          data.forEach((slot) => {
            const startTime = convertUTCToLocal(slot.appointment_start);
            const endTime = convertUTCToLocal(slot.appointment_end);

            let currentTime = new Date(startTime);
            while (currentTime < endTime) {
              excludedTimes.push(new Date(currentTime));
              currentTime = new Date(currentTime.getTime() + timeInterval * 60000);
            }
          });

          setBookedSlots(excludedTimes);
        } else {
          setBookedSlots([]);
        }
      }
    } catch (error) {
      logger.error("Error fetching booked slots", error);
      toast.error("Error fetching available slots");
    }
  };

  // Initial load - fetch booked slots when reschedule modal opens
  useEffect(() => {
    if (rescheduleAppt) {
      fetchBookedSlots(rescheduleAppt, currentDate);
    }
  }, [rescheduleAppt]);

  // Refetch when user selects a date in the reschedule modal
  useEffect(() => {
    if (rescheduleDate && rescheduleAppt) {
      fetchBookedSlots(rescheduleAppt, rescheduleDate);
    }
  }, [rescheduleDate?.toDateString()]);

  // Filter by selected date first, then by status
  const filteredAppointments = appointments.filter(appt => {
    // Filter by selected date if one is set
    if (selectedDate) {
      const appointmentTime = appt.appointment_start || appt.scheduled_time;
      if (!appointmentTime) return false;
      
      const apptDate = convertUTCToLocal(appointmentTime);
      logger.log('Before conversion: ', appointmentTime)
      logger.log('After conversion: ', apptDate)
      const matchesDate = apptDate.getDate() === selectedDate.getDate() &&
                         apptDate.getMonth() === selectedDate.getMonth() &&
                         apptDate.getFullYear() === selectedDate.getFullYear();
      
      if (!matchesDate) return false;
    }

    // Then filter by derived appointment status.
    // "Pending" = on the schedule but not yet checked out; "Complete" = checked out.
    const statusKey = getAppointmentDisplayStatus(appt).key;
    if (filter === 'pending') return statusKey !== 'checked-out' && statusKey !== 'cancelled' && statusKey !== 'no-show';
    if (filter === 'complete') return statusKey === 'checked-out';
    return statusKey !== 'cancelled';
  });

  // Get counts for filter buttons
  const getCounts = () => {
    const dateFilteredAppts = selectedDate 
      ? appointments.filter(appt => {
          const appointmentTime = appt.appointment_start || appt.scheduled_time;
          if (!appointmentTime) return false;
          const apptDate = convertUTCToLocal(appointmentTime);
          return apptDate.getDate() === selectedDate.getDate() &&
                 apptDate.getMonth() === selectedDate.getMonth() &&
                 apptDate.getFullYear() === selectedDate.getFullYear();
        })
      : appointments;

    const nonCancelledAppts = dateFilteredAppts.filter(a => {
      const k = getAppointmentDisplayStatus(a).key;
      return k !== 'cancelled' && k !== 'no-show';
    });
    return {
      all: nonCancelledAppts.length,
      pending: nonCancelledAppts.filter(a => getAppointmentDisplayStatus(a).key !== 'checked-out').length,
      complete: nonCancelledAppts.filter(a => getAppointmentDisplayStatus(a).key === 'checked-out').length
    };
  };

  const counts = getCounts();

  return (
    <div className="appointments-list-section">
      <div className="list-header">
        <h3 className="section-title">Scheduled Appointments</h3>
        <div className="filter-pills">
          <button 
            className={filter === 'all' ? 'active' : ''} 
            onClick={() => setFilter('all')}
          >
            All ({counts.all})
          </button>
          <button 
            className={filter === 'pending' ? 'active' : ''} 
            onClick={() => setFilter('pending')}
          >
            Pending ({counts.pending})
          </button>
          <button 
            className={filter === 'complete' ? 'active' : ''} 
            onClick={() => setFilter('complete')}
          >
            Complete ({counts.complete})
          </button>
        </div>
      </div>

      {filteredAppointments.length > 0 ? (
        <div className="calendar-appointments-grid">
          {filteredAppointments.map((appt, index) => {
            // Use new fields with fallback to legacy
            const startTime = appt.appointment_start || appt.scheduled_time;
            const endTime = appt.appointment_end;
            const apptStatus = getAppointmentDisplayStatus(appt);

            return (
              <div key={index} className={`appointment-card status-${apptStatus.key}`}>
                <div className="appointment-header">
                  <div className="appointment-patient">
                    <PatientAvatar
                      url={avatars[appt.ris_patients_id]}
                      size={44}
                      alt={`${appt.first_name || ''} ${appt.last_name || ''}`.trim() || 'Patient'}
                    />
                    <div>
                      <h4>
                        <span
                          onClick={() => {
                            setCurrentPatient({ ris_patients_id: appt.ris_patients_id });
                            navigate('/patientAbout');
                          }}
                          style={{ textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {appt.first_name} {appt.last_name}
                        </span>
                      </h4>
                      <span className={`status-badge ${apptStatus.key}`}>
                        {apptStatus.label}
                      </span>
                    </div>
                  </div>
                  <div className="appointment-actions">
                    {appt.visit_type !== 'Lab' && (
                      <button
                        onClick={() => { setEditAppt(appt); setEditBillInsurance(!!appt.bill_insurance); }}
                        className="icon-btn"
                        title="Edit Appointment"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {apptStatus.key === 'checked-in' && (
                      <button
                        onClick={() => handleCheckout(appt)}
                        className="icon-btn checkout"
                        title="Check Out"
                        disabled={checkingOut === appt.id}
                      >
                        <LogOut size={16} />
                      </button>
                    )}
                    {!appt.check_in_time && (
                      <>
                        <button
                          onClick={() => setRescheduleAppt(appt)}
                          className="icon-btn"
                          title="Reschedule"
                        >
                          <CalendarClock />
                        </button>
                        {appt.status !== 'Cancelled' && (
                          <button
                            onClick={() => onCancel(appt)}
                            className="icon-btn danger"
                            title="Cancel"
                          >
                            <CalendarOff />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="appointment-details">
                  {appt.phone_number && (
                    <div className="detail-row">
                      <span className="label">Phone:</span>
                      <span className="value">{appt.phone_number}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="label">Type:</span>
                    <span className="value">{appt.visit_type}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Exam:</span>
                    <span className="value">{appt.exam}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Date:</span>
                    <span className="value">
                      {formatUTCForDisplay(startTime, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Time:</span>
                    <span className="value">
                      {endTime ? (
                        formatAppointmentTimeRange(startTime, endTime)
                      ) : (
                        formatTimeForDisplay(startTime)
                      )}
                    </span>
                  </div>
                  {appt.visit_type !== 'Lab' && (
                    <div className="detail-row">
                      <span className="label">Billing:</span>
                      <span className="value" style={{ color: appt.bill_insurance ? '#0e8a8f' : '#6b7280', fontWeight: 600 }}>
                        {appt.bill_insurance ? 'Insurance' : 'Self-Pay'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Screening consultation status — only shown for Screening appointments */}
                {appt.visit_type === 'Screening' && (
                  <div className="consultation-status-section">
                    <span className="consultation-label">Screening Consultation</span>

                    {/* Current status badge */}
                    {appt.consultation_status && (
                      <div
                        className="consultation-status-badge"
                        style={{ color: CONSULTATION_STATUS_LABELS[appt.consultation_status]?.color }}
                      >
                        {CONSULTATION_STATUS_LABELS[appt.consultation_status]?.label}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="consultation-actions">
                      <button
                        className={`consult-btn completed ${appt.consultation_status === 'completed' ? 'active' : ''}`}
                        title="Patient received consultation today"
                        disabled={consultationUpdating === appt.id}
                        onClick={() => { setFollowUpAppt(null); setFollowUpDate(null); handleConsultationStatus(appt, 'completed'); }}
                      >
                        ✓ Completed
                      </button>
                      {appt.exam !== 'Consultation' && (
                        <button
                          className={`consult-btn scheduled ${appt.consultation_status === 'scheduled' ? 'active' : ''}`}
                          title="Patient needs to come back — pick a follow-up time"
                          disabled={consultationUpdating === appt.id}
                          onClick={() => {
                            setFollowUpDate(null);
                            setFollowUpAppt(followUpAppt?.id === appt.id ? null : appt);
                          }}
                        >
                          📅 Needs Follow-up
                        </button>
                      )}
                      <button
                        className={`consult-btn declined ${appt.consultation_status === 'declined' ? 'active' : ''}`}
                        title="Patient declined consultation"
                        disabled={consultationUpdating === appt.id}
                        onClick={() => { setFollowUpAppt(null); setFollowUpDate(null); handleConsultationStatus(appt, 'declined'); }}
                      >
                        ✕ Declined
                      </button>
                    </div>

                    {/* Inline follow-up date picker — shown when Needs Follow-up is active */}
                    {followUpAppt?.id === appt.id && (
                      <div className="followup-picker">
                        <span className="schedule-date-legend">Greyed-out dates are unavailable — clinic closed.</span>
                        <DatePicker
                          selected={followUpDate}
                          onChange={(date) => {
                            setFollowUpDate(date);
                            // Re-fetch booked slots when the selected date changes
                            if (date && (!followUpDate || date.toDateString() !== followUpDate.toDateString())) {
                              fetchConsultationBookedSlots(date);
                            }
                          }}
                          onCalendarOpen={() => fetchConsultationBookedSlots(followUpDate || new Date())}
                          onMonthChange={fetchClosedDates}
                          showTimeSelect
                          minTime={APPOINTMENT_MIN_TIME}
                          maxTime={APPOINTMENT_MAX_TIME}
                          timeIntervals={10}
                          dateFormat="MMM d, yyyy h:mm aa"
                          className="modern-datepicker"
                          minDate={new Date()}
                          placeholderText="Select follow-up date & time"
                          excludeTimes={consultationBookedSlots}
                          filterDate={(date) => !isClosedDate(date)}
                          dayClassName={(date) => isClosedDate(date) ? 'schedule-day-unavailable' : undefined}
                          renderDayContents={(dayOfMonth, date) => (
                            <span title={date && isClosedDate(date) ? 'Clinic closed' : undefined}>{dayOfMonth}</span>
                          )}
                        />
                        <div className="followup-picker-actions">
                          <button
                            className="consult-btn scheduled active"
                            disabled={!followUpDate || consultationUpdating === appt.id}
                            onClick={handleScheduleFollowUp}
                          >
                            {consultationUpdating === appt.id ? 'Scheduling…' : 'Confirm'}
                          </button>
                          <button
                            className="consult-btn"
                            onClick={() => { setFollowUpAppt(null); setFollowUpDate(null); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <p>No appointments found</p>
        </div>
      )}

      {rescheduleAppt && (
        <RescheduleModal
          appointment={rescheduleAppt}
          onClose={() => {
            setRescheduleAppt(null);
            setRescheduleDate(null);
            setBookedSlots([]);
          }}
          onReschedule={(appt, newDate) => {
            onReschedule(appt, newDate);
            setRescheduleAppt(null);
            setRescheduleDate(null);
            setBookedSlots([]);
          }}
          bookedSlots={bookedSlots}
          newDate={rescheduleDate}
          onDateChange={setRescheduleDate}
        />
      )}

      {editAppt && (
        <div className="modal-overlay" onClick={() => setEditAppt(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Appointment</h3>
              <button onClick={() => setEditAppt(null)} className="close-btn">✕</button>
            </div>
            <div className="modal-body">
              <div className="current-appointment-info">
                <p><strong>{editAppt.first_name} {editAppt.last_name}</strong></p>
                <p>{editAppt.visit_type} — {editAppt.exam || 'N/A'}</p>
                <p>{formatUTCForDisplay(editAppt.appointment_start)}</p>
              </div>
              <div className="form-field" style={{ marginTop: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none', fontSize: '15px' }}>
                  <input
                    type="checkbox"
                    checked={editBillInsurance}
                    onChange={e => setEditBillInsurance(e.target.checked)}
                    style={{ width: '17px', height: '17px', cursor: 'pointer', accentColor: '#0e8a8f' }}
                  />
                  Bill Insurance
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setEditAppt(null)} className="secondary-btn" disabled={editSaving}>Cancel</button>
              <button onClick={handleEditSave} className="primary-btn" disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main Scheduling Component
function Scheduling() {
  const [view, setView] = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [allAppointments, setAllAppointments] = useState([]);
  // Keyed by 'YYYY-MM-DD' -> { blockedModalities, allLabsBlocked, blockedSpecimens } —
  // see AdminRadTechAvailability / AdminLabMachineAvailability under the Admin Dashboard.
  const [monthAvailability, setMonthAvailability] = useState({});
  // Default to today's appointments; the calendar's "Show All" button clears this
  // (sets it to null) to show every appointment for the month.
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { currentUser } = useContext(GlobalStateContext);
  const user = getUserId(currentUser);

  useEffect(() => {
    if (!currentUser) return;
    fetchMonthAppointments();
    fetchMonthAvailability();
  }, [currentMonth, currentUser]);

  const fetchMonthAppointments = async () => {
    const data = await api.getMonthOrders(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      user,
      currentUser.type
    );
    setAllAppointments(data);
  };

  const fetchMonthAvailability = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const [modalityRes, labRes, closureRes] = await Promise.all([
        fetch(`${BASE_URL}/scheduling/modalityAvailability?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/labAvailability?startDate=${startDate}&endDate=${endDate}`),
        fetch(`${BASE_URL}/scheduling/clinicClosures?startDate=${startDate}&endDate=${endDate}`),
      ]);
      const modalityData = modalityRes.ok ? await modalityRes.json() : {};
      const labData = labRes.ok ? await labRes.json() : {};
      const closureData = closureRes.ok ? await closureRes.json() : [];

      const merged = {};
      Object.keys(modalityData).forEach(date => {
        merged[date] = {
          ...(merged[date] || {}),
          blockedModalities: modalityData[date].blockedModalities,
        };
      });
      Object.keys(labData).forEach(date => {
        merged[date] = {
          ...(merged[date] || {}),
          allLabsBlocked: labData[date].allBlocked,
          blockedSpecimens: labData[date].blockedSpecimens,
        };
      });
      // Clinic closures (carried over from Health Hub's AdminBlockedDates) close
      // the whole day — every visit type, not just Radiology/Lab.
      closureData.forEach(c => {
        const date = c.blocked_date.split('T')[0];
        merged[date] = { ...(merged[date] || {}), clinicClosed: true, closureReason: c.reason };
      });
      setMonthAvailability(merged);
    } catch (error) {
      logger.error('Error fetching month availability', error);
    }
  };

  const handleReschedule = async (appointment, newDate) => {
    try {
      logger.log(newDate)
      const response = await fetch(`${BASE_URL}/scheduling/reschedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: appointment.id,
          appointment_start: convertLocalToUTC(newDate),
          exam_type: appointment.exam,
          visit_type: appointment.visit_type,
          user,
          type: currentUser.type
        })
      });

      if (response.ok) {
        const data = await response.json();
        logger.log(data)
        toast.success('Appointment rescheduled successfully!');
        fetchMonthAppointments();
      } else {
        toast.error('Failed to reschedule appointment');
      }
    } catch (error) {
      logger.error('Error rescheduling appointment:', error);
      toast.error('Error rescheduling appointment');
    }
  };

  const handleCancel = async (appointment) => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      try {
        const response = await fetch(`${BASE_URL}/scheduling/cancel`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: appointment.id,
            user,
            type: currentUser.type
          })
        });

        if (response.ok) {
          toast.success('Appointment cancelled successfully!');
          fetchMonthAppointments();
        } else {
          toast.error('Failed to cancel appointment');
        }
      } catch (error) {
        logger.error('Error cancelling appointment:', error);
        toast.error('Error cancelling appointment');
      }
    }
  };

  const handleScheduleComplete = () => {
    fetchMonthAppointments();
  };

  // Filter appointments for display
  const getFilteredAppointments = () => {
    if (view !== 'calendar') return allAppointments;

    // If a date is selected, only show appointments for that date
    if (selectedDate) {
      return allAppointments.filter(appt => {
        const appointmentTime = appt.appointment_start || appt.scheduled_time;
        if (!appointmentTime) return false;
        
        const apptDate = convertUTCToLocal(appointmentTime);
        return apptDate.getDate() === selectedDate.getDate() &&
               apptDate.getMonth() === selectedDate.getMonth() &&
               apptDate.getFullYear() === selectedDate.getFullYear();
      });
    }

    // Otherwise, show all appointments for the current month
    return allAppointments.filter(appt => {
      const appointmentTime = appt.appointment_start || appt.scheduled_time;
      if (!appointmentTime) return false;
      
      const apptDate = convertUTCToLocal(appointmentTime);
      return apptDate.getMonth() === currentMonth.getMonth() &&
             apptDate.getFullYear() === currentMonth.getFullYear();
    });
  };

  return (
    <div className="scheduling-page">
    <div className="modern-scheduling-container">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <PageTopBar links={[
        { label: 'Home', to: '/home', icon: Home },
        { label: 'Check-In', to: '/checkIn', icon: UserCheck },
      ]} />
      <div className="page-header">
        <h1 className="page-title">Patient Scheduling</h1>
      </div>

      <div className="view-tabs">
        <button
          className={view === 'calendar' ? 'active' : ''}
          onClick={() => setView('calendar')}
        >
          <CalendarDays /> Calendar
        </button>
        <button
          className={view === 'week' ? 'active' : ''}
          onClick={() => setView('week')}
        >
          <CalendarClock /> Week View
        </button>
        <button
          className={view === 'screenings' ? 'active' : ''}
          onClick={() => setView('screenings')}
        >
          <CalendarHeart /> Screenings
        </button>
        <button
          className={view === 'referrals' ? 'active' : ''}
          onClick={() => setView('referrals')}
        >
          <DoorOpen /> Radiology Referrals
        </button>
        <button
          className={view === 'labReferrals' ? 'active' : ''}
          onClick={() => setView('labReferrals')}
        >
          <FlaskConical /> Lab Referrals
        </button>
      </div>

      {view === 'calendar' && (
        <div className="calendar-view">
          <CalendarView
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            appointments={allAppointments}
            onDateClick={(date) => {
              // Don't change month, just filter
            }}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            monthAvailability={monthAvailability}
          />
          <AppointmentsList
            appointments={getFilteredAppointments()}
            onReschedule={handleReschedule}
            onCancel={handleCancel}
            selectedDate={selectedDate}
            onRefresh={fetchMonthAppointments}
          />
        </div>
      )}

      {view === 'week' && (
        <div className="week-view">
          <WeekView
            currentWeek={currentWeek}
            setCurrentWeek={setCurrentWeek}
          />
        </div>
      )}

      {view === 'screenings' && (
        <div className="screenings-view">
          <ScreeningPackages onScheduleComplete={handleScheduleComplete} />
        </div>
      )}

      {view === 'referrals' && (
        <div className="referrals-view">
          <NewImagingOrder />
        </div>
      )}

      {view === 'labReferrals' && (
        <div className="referrals-view">
          <LabReferralView onComplete={handleScheduleComplete} />
        </div>
      )}
    </div>
    </div>
  );
}

export default Scheduling;